import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AccountDeletionRequests } from "./AccountDeletionRequests";
import { BIManagedBanner } from "./BIManagedBanner";

const roleColors = {
  admin: "bg-purple-50 text-purple-700 border-purple-200",
  owner: "bg-zinc-900 text-white border-zinc-800",
  manager: "bg-blue-50 text-blue-700 border-blue-200",
  branch_manager: "bg-cyan-50 text-cyan-700 border-cyan-200",
  dispatcher: "bg-emerald-50 text-emerald-700 border-emerald-200",
  vendor: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

const roleLabels = {
  admin: "Admin",
  owner: "Owner",
  manager: "Manager",
  branch_manager: "Branch Manager",
  dispatcher: "Dispatcher",
  vendor: "Technician",
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [locations, setLocations] = useState([]);
  const [newUser, setNewUser] = useState({
    email: "", password: "", role: "vendor", first_name: "", last_name: "", location_id: ""
  });

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (filterRole !== "all") params.set("role", filterRole);
      params.set("limit", "50");
      const { data } = await API.get(`/management/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const { data } = await API.get("/management/locations");
      setLocations(data.locations);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchUsers(); }, [filterRole]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = { ...newUser };
      if (!payload.location_id) payload.location_id = null;
      await API.post("/management/users", payload);
      toast.success("User created");
      setShowCreate(false);
      setNewUser({ email: "", password: "", role: "vendor", first_name: "", last_name: "", location_id: "" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const openCreate = () => {
    fetchLocations();
    setShowCreate(true);
  };

  const needsLocation = newUser.role === "branch_manager" || newUser.role === "vendor";

  return (
    <div className="space-y-4" data-testid="user-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Users & Roles</h1>
          <p className="text-sm text-zinc-500">{total} users total</p>
        </div>
      </div>

      <BIManagedBanner />

      <AccountDeletionRequests />

      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-user-role">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="branch_manager">Branch Manager</SelectItem>
              <SelectItem value="dispatcher">Dispatcher</SelectItem>
              <SelectItem value="vendor">Technician</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border border-zinc-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Email</TableHead>
                <TableHead className="text-xs">Role</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Status</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Last Login</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-400">No users found</TableCell></TableRow>
              ) : users.map(u => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm text-zinc-900">{u.email}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${roleColors[u.role] || ""}`}>
                      {roleLabels[u.role] || u.role}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-zinc-500">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-zinc-500">
                    {new Date(u.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Email</Label>
              <Input value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                type="email" data-testid="new-user-email" className="mt-1" /></div>
            <div><Label className="text-xs">Password</Label>
              <Input value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                type="password" data-testid="new-user-password" className="mt-1" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor">Technician</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher</SelectItem>
                  <SelectItem value="branch_manager">Branch Manager</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select></div>
            {needsLocation && (
              <div><Label className="text-xs">Assigned Location</Label>
                <Select value={newUser.location_id || "none"} onValueChange={v => setNewUser(p => ({ ...p, location_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="mt-1" data-testid="new-user-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name} - {loc.city}, {loc.state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">First Name</Label>
                <Input value={newUser.first_name} onChange={e => setNewUser(p => ({ ...p, first_name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Last Name</Label>
                <Input value={newUser.last_name} onChange={e => setNewUser(p => ({ ...p, last_name: e.target.value }))} className="mt-1" /></div>
            </div>
            <Button onClick={handleCreate} disabled={creating || !newUser.email || !newUser.password}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="submit-new-user">
              {creating ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Create User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
